import { useMemo, useState } from 'react'
import classnames from 'classnames'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'

import i18n from '../../../locales/i18n'
import './form-description.scss'

const DESCRIPTION_MAX_LENGTH = 10000

type Props = {
    description: string
    changeDescription: (html: string) => void
    changeDescriptionPlain?: (plainText: string) => void
    maxLength?: number
    validationErrors?: { description?: string }
    classNames?: string
}

const TOOLBAR_MODULES = {
    toolbar: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
}

const TOOLBAR_FORMATS = [
    'header',
    'bold',
    'italic',
    'strike',
    'list',
    'link',
]

const FormDescription = ({
    description,
    changeDescription,
    changeDescriptionPlain,
    maxLength = DESCRIPTION_MAX_LENGTH,
    validationErrors,
    classNames,
}: Props) => {
    const [textLength, setTextLength] = useState<number>(0)

    const modules = useMemo(() => TOOLBAR_MODULES, [])
    const formats = useMemo(() => TOOLBAR_FORMATS, [])

    const handleChange = (
        html: string,
        _delta: unknown,
        _source: unknown,
        editor: { getText: () => string },
    ) => {
        const plain = editor.getText().replace(/\n+$/, '')

        setTextLength(plain.length)
        changeDescription(html)

        if (changeDescriptionPlain) {
            changeDescriptionPlain(plain)
        }
    }

    const exceeded = textLength > maxLength

    return (
        <div className={classnames('d-flex flex-column', { [classNames ?? '']: !!classNames })}>
            <label className={classnames({ 'text-danger': exceeded })}>
                {textLength} / {maxLength}
            </label>

            {exceeded && (
                <span className="text-danger">
                    {i18n.t('limitExceeded', { count: maxLength })}
                </span>
            )}

            <ReactQuill
                theme="snow"
                defaultValue={description}
                onChange={handleChange}
                modules={modules}
                formats={formats}
            />

            {validationErrors?.description && (
                <span className="text-danger mt-1">{validationErrors.description}</span>
            )}
        </div>
    )
}

export default FormDescription
